import { AxiosError } from "axios";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";
import useSWRInfinite from "swr/infinite";
import { AuthContext } from "../../../contexts/AuthContext";
import { WebsocketContext } from "../../../contexts/WebsocketAuthContext";
import { APIEndpointEnum } from "../../../enums/endpoint";
import { apiFetcher } from "../../../services/api/apiFetcher";
import { isStaleApiCredentialError } from "../../../utils/authError";
import { PositionWithIdentity } from "../../../utils/positionIdentity";
import {
  classifyRows,
  SETTLING_FLAG,
  getSyncStatuses,
  subscribeSyncStatus,
  getSnapshot as getPendingClosesSnapshot,
  observeRows as observePendingCloseRows,
  subscribe as subscribePendingCloses,
} from "../../../services/tradeSync";
import {
  pickCount,
  pickCursor,
  pickHasNext,
  pickItems,
} from "./useCursorPaginatedHistory.helpers";
import {
  AnyResponse,
  NormalizedPage,
  UseCursorPaginatedHistoryOptions,
  UseCursorPaginatedHistoryResult,
} from "./useCursorPaginatedHistory.types";
import { useCursorPaginatedHistoryRealtime } from "./useCursorPaginatedHistoryRealtime";
import { useAccountKeepPreviousData } from "../useAccountKeepPreviousData";

const DEFAULT_PAGE_SIZE = 25;

/** Shared constant so the default status keeps a stable identity. */
const LIVE_STATUS = { state: "LIVE" } as const;

// ─── TEMPORARY TEST OVERRIDE ────────────────────────────────────────────
// Set to a hex address to bypass AuthContext and force every history hook
// to query that address (useful for testing cursor pagination without
// connecting a wallet). Set to undefined to disable the override and use
// the real connected account.
export const TEST_ACCOUNT_OVERRIDE: string | undefined = undefined; // "0x39efb1177c3ed256d28f78ae6092d1b6595c3521";
// ────────────────────────────────────────────────────────────────────────

/**
 * Cursor-paginated history hook (SWR-Infinite based).
 *
 * Expected response shape:
 *   {
 *     <itemsKey>: T[],            // e.g. order_history / trade_history / position_history
 *     pagination: {
 *       next_cursor?: string,
 *       has_next: boolean,
 *       total_count?: number,
 *       ...
 *     }
 *   }
 *
 * Also accepts `{ data: T[], next_cursor }` and bare `T[]` for endpoints that
 * have not yet been wrapped. `hasMore` only flips on signals the backend
 * explicitly sends — never on row-count heuristics.
 */
export function useCursorPaginatedHistory<T>({
  endpoint,
  itemsKey,
  pageSize = DEFAULT_PAGE_SIZE,
  query,
  realtimeChannels,
  enabled = true,
}: UseCursorPaginatedHistoryOptions): UseCursorPaginatedHistoryResult<T> {
  const { apiKey, apiSecret, account, deleteAuthInfo } =
    useContext(AuthContext);
  const { authenticated, triggerSubscribe, triggerUnsubscribe, lastMessages } =
    useContext(WebsocketContext);

  // When TEST_ACCOUNT_OVERRIDE is set, ignore the connected wallet and
  // hit the API anonymously for the override address. The dev endpoints
  // require only the `?account=` query param.
  const accountKey =
    TEST_ACCOUNT_OVERRIDE?.toLowerCase() ?? account?.toLowerCase();
  const isPositionsEndpoint = endpoint === APIEndpointEnum.POSITIONS;
  const isOpenOrdersEndpoint = endpoint === APIEndpointEnum.OPEN_ORDERS;

  const queryString = useMemo(() => {
    if (!query) return "";
    return Object.entries(query)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .sort(([a], [b]) => a.localeCompare(b))
      .map(
        ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
      )
      .join("&");
  }, [query]);

  // Credentials are optional when the test override is active — the
  // three history endpoints are account-scoped via `?account=`, not the
  // API-KEY / API-SECRET headers, so we only require an account key.
  const isReady = Boolean(
    enabled && accountKey && (TEST_ACCOUNT_OVERRIDE || (apiKey && apiSecret)),
  );

  const buildUrl = useCallback(
    (cursor?: string) => {
      const path = String(endpoint).replace(/^\/+/, "");
      const params = new URLSearchParams();
      params.set("account", accountKey || "");
      params.set("limit", String(pageSize));
      if (cursor) params.set("cursor", cursor);
      if (queryString) {
        new URLSearchParams(queryString).forEach((value, key) => {
          params.set(key, value);
        });
      }
      return `${path}?${params.toString()}`;
    },
    [accountKey, endpoint, pageSize, queryString],
  );

  const fetchPage = useCallback(
    async (cursor?: string): Promise<NormalizedPage<T>> => {
      const url = buildUrl(cursor);
      const raw = await apiFetcher<AnyResponse<T>>({
        url,
        method: "GET",
        apiKey,
        apiSecret,
      });
      return {
        data: pickItems<T>(raw, itemsKey),
        nextCursor: pickCursor<T>(raw),
        hasNext: pickHasNext<T>(raw),
        count: pickCount<T>(raw),
      };
    },
    [apiKey, apiSecret, buildUrl, itemsKey],
  );

  // The cache holds RAW server truth. Hiding a locally-closed position is a
  // read-time projection applied to `items` below, never a filter here and
  // never a cache write.
  //
  // Two reasons. First, a filter here can only see one page, so it cannot tell
  // whether a match is unique — and hiding a row we cannot uniquely identify
  // removes a position the trader still holds. Second, if the closed row were
  // stripped before reaching SWR, falling back to server truth would be
  // impossible: the truth would already be gone.
  const fetchFilteredPage = fetchPage;

  const getKey = useCallback(
    (pageIndex: number, previousPageData: NormalizedPage<T> | null) => {
      if (!isReady) return null;
      if (pageIndex === 0) {
        return [
          endpoint,
          accountKey,
          apiKey,
          apiSecret,
          pageSize,
          queryString,
          0,
          undefined,
        ];
      }
      // Backend says no more pages — stop.
      if (previousPageData?.hasNext === false) return null;
      const previousCursor = previousPageData?.nextCursor;
      if (!previousCursor) return null;
      return [
        endpoint,
        accountKey,
        apiKey,
        apiSecret,
        pageSize,
        queryString,
        pageIndex,
        previousCursor,
      ];
    },
    [accountKey, apiKey, apiSecret, endpoint, isReady, pageSize, queryString],
  );

  const { keepPreviousData, trackData } = useAccountKeepPreviousData(accountKey);

  const swr = useSWRInfinite<NormalizedPage<T>, AxiosError>(
    getKey,
    (key) => {
      const cursor = (key as unknown[])?.[7] as string | undefined;
      return fetchFilteredPage(cursor);
    },
    {
      revalidateFirstPage: true,
      revalidateOnFocus: false,
      // Keep previous data (and paginated pages) only while an account is
      // selected — clears the laggy history on disconnect (see useOrderController)
      // and on a wallet switch, until the new account's pages arrive (FE-14).
      keepPreviousData,
      shouldRetryOnError: false,
      errorRetryCount: 0,
      dedupingInterval: 5000,
      onError: (e) => {
        const error = e as AxiosError;
        const responseData = error.response?.data as any;
        const isInvalidToken =
          responseData?.trim?.() === "INVALID_TOKEN" ||
          responseData?.error === "INVALID_TOKEN";
        if (isInvalidToken || isStaleApiCredentialError(e)) {
          deleteAuthInfo("api");
        }
      },
    },
  );

  trackData(swr.data);
  const pages = swr.data || [];
  const lastPage = pages[pages.length - 1];
  // Only trust signals the backend explicitly sends:
  //   1. `pagination.has_next` — definitive boolean
  //   2. presence of `pagination.next_cursor` — implies more pages exist
  // If neither is present, treat the list as exhausted (no guessing).
  let hasMore = false;
  if (lastPage) {
    if (lastPage.hasNext !== undefined) {
      hasMore = lastPage.hasNext;
    } else if (lastPage.nextCursor) {
      hasMore = true;
    }
  }

  // ── Locally-closed positions ────────────────────────────────────────────
  // Read-time projection over raw server rows. `classifyRows` hides a row only
  // when it is the UNIQUE match for a close the trader submitted; when two rows
  // could be the one closed, it hides neither and shows server truth instead.
  const swrData = swr.data;
  const pendingCloseVersion = useSyncExternalStore(
    subscribePendingCloses,
    getPendingClosesSnapshot,
    getPendingClosesSnapshot,
  );

  const rawItems = useMemo(
    () => (swrData || []).flatMap((page) => page.data),
    [swrData],
  );

  const hiddenCount = useMemo(() => {
    if (!isPositionsEndpoint || !accountKey) return 0;
    return classifyRows(accountKey, rawItems as PositionWithIdentity[], !hasMore)
      .hidden.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawItems, isPositionsEndpoint, accountKey, hasMore, pendingCloseVersion]);

  const items = useMemo(() => {
    if (!isPositionsEndpoint || !accountKey) return rawItems;
    const { hidden, settling } = classifyRows(
      accountKey,
      rawItems as PositionWithIdentity[],
      !hasMore,
    );
    if (!hidden.length && !settling.length) return rawItems;
    const hiddenIndices = new Set(hidden);
    const settlingIndices = new Set(settling);
    // Tag against the ORIGINAL indices first, then drop hidden rows — filtering
    // first would shift every index out from under the settling set.
    return rawItems
      .map((item, index) =>
        settlingIndices.has(index)
          ? ({ ...(item as object), [SETTLING_FLAG]: true } as T)
          : item,
      )
      .filter((_, index) => !hiddenIndices.has(index));
    // `pendingCloseVersion` is the external-store subscription: the registry
    // changing must recompute this even though `rawItems` has not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawItems, isPositionsEndpoint, accountKey, hasMore, pendingCloseVersion]);

  // Record what the server actually returned. This is what ages a pending close
  // toward "unresolved" and releases it when a row proves to be a different
  // position. It must run in an effect — never during render or inside the
  // fetcher — because it writes to the shared registry.
  useEffect(() => {
    if (!isPositionsEndpoint || !accountKey || !swrData) return;
    observePendingCloseRows(
      accountKey,
      rawItems as PositionWithIdentity[],
      !hasMore,
    );
  }, [rawItems, swrData, isPositionsEndpoint, accountKey, hasMore]);

  const targetId = `${endpoint}:${accountKey || "anon"}:${queryString}`;
  const syncStatuses = useSyncExternalStore(
    subscribeSyncStatus,
    getSyncStatuses,
    getSyncStatuses,
  );
  const syncStatus = syncStatuses[targetId] || LIVE_STATUS;

  const isLoading = !swr.data && !swr.error && isReady;
  const isLoadingMore = Boolean(
    swr.isValidating && swr.size > 0 && swr.data && swr.size > swr.data.length,
  );

  const swrSetSize = swr.setSize;
  const swrSize = swr.size;
  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return;
    swrSetSize(swrSize + 1);
  }, [hasMore, isLoadingMore, swrSetSize, swrSize]);

  const reset = useCallback(() => {
    swrSetSize(1);
  }, [swrSetSize]);

  // `swr.mutate` is stable per-hook-instance in SWR 2.x, so it's safe to
  // depend on it directly rather than the whole `swr` object (which is a
  // new reference each render).
  const swrMutate = swr.mutate;

  useCursorPaginatedHistoryRealtime<T>({
    swrMutate,
    // `isReady`, not `enabled`: the fetch also requires credentials, and arming
    // convergence without them meant every trade burned its full budget against
    // a null SWR key and published a STALE that proved nothing.
    isReady,
    // The live pages, so a post-trade convergence run compares the server
    // against what the trader is actually looking at.
    pages: swrData,
    enabled,
    authenticated,
    realtimeChannels,
    triggerSubscribe,
    triggerUnsubscribe,
    lastMessages,
    accountKey,
    endpoint,
    queryString,
    isPositionsEndpoint,
    isOpenOrdersEndpoint,
  });

  // ── CSV-export helper: walks every cursor once, no SWR cache writes ────
  const fetchAllPages = useCallback(
    async (max = 5000): Promise<T[]> => {
      const all: T[] = [];
      let cursor: string | undefined;
      // Hard upper bound on the number of pages to prevent runaway loops.
      for (let i = 0; i < 200; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const page = await fetchFilteredPage(cursor);
        all.push(...page.data);
        if (all.length >= max) return all.slice(0, max);
        if (page.hasNext === false) return all;
        if (!page.nextCursor) {
          // No has_next signal and no cursor: backend hasn't shipped cursors
          // yet for this endpoint — bail to avoid an infinite loop.
          return all;
        }
        cursor = page.nextCursor;
      }
      return all;
    },
    [fetchFilteredPage],
  );

  return {
    items,
    pages,
    isLoading,
    isLoadingMore,
    isValidating: swr.isValidating,
    hasMore,
    totalCount: lastPage?.count,
    hiddenCount,
    loadMore,
    reset,
    mutate: swr.mutate as unknown as () => Promise<unknown>,
    sync: syncStatus,
    error: swr.error,
    fetchAllPages,
  };
}
